import { getEmailCatalog } from '../email-builder/catalog';
import type { EmailRenderContext } from '../email-builder/catalog';
import type { PersistedEmailBlock } from '../email-builder/useEmailBlockTree';

export interface EmailTemplateData {
  storeName: string;
  logoUrl?: string;
  iconUrl?: string;
  headline: string;
  paragraph: string;
  subText?: string;
  buttonText: string;
  buttonLink: string;
  buttonBackground: string;
  buttonBorderRadius: number;
  buttonFontColor: string;
  buttonFontStyle: 'none' | 'uppercase' | 'capitalize';
  headlineDesktopFontColor: string;
  headlineDesktopFontFamily: string;
  headlineDesktopFontSize: number;
  headlineDesktopFontWeight: number;
  headlineFontStyle: 'none' | 'uppercase' | 'capitalize';
  headlineDesktopLineHeight: number;
  headlineDesktopLetterSpacing: number;
  paragraphDesktopFontColor: string;
  paragraphDesktopFontFamily: string;
  paragraphDesktopFontSize: number;
  paragraphDesktopFontWeight: number;
  paragraphFontStyle: 'none' | 'uppercase' | 'capitalize';
  paragraphDesktopLineHeight: number;
  paragraphDesktopLetterSpacing: number;
  subTextDesktopFontColor: string;
  subTextDesktopFontFamily: string;
  subTextDesktopFontSize: number;
  subTextDesktopFontWeight: number;
  subTextDesktopFontStyle: 'none' | 'uppercase' | 'capitalize';
  subTextDesktopLineHeight: number;
  subTextDesktopLetterSpacing: number;
  headlineMobileFontColor: string;
  headlineMobileFontFamily: string;
  headlineMobileFontSize: number;
  headlineMobileFontWeight: number;
  paragraphMobileFontColor: string;
  paragraphMobileFontFamily: string;
  paragraphMobileFontSize: number;
  paragraphMobileFontWeight: number;
  desktopLogoWidth: number;
  mobileLogoWidth: number;
  footerContent: string;
  togglePoweredByOpEnabled: boolean;
  selectedTemplateKey: string;
  customDomain: string;
  isPreview: boolean;
}

// Default "your logo here" placeholder shown until a merchant uploads a logo (mirrored in email-builder/catalog).
const DEFAULT_LOGO_SVG = `<img src="https://st5.depositphotos.com/69915036/62675/v/450/depositphotos_626754468-stock-illustration-your-logo-here-placeholder-symbol.jpg" alt="Your logo here" style="display: block; width: 100%; max-width: 100%; height: auto; object-fit: contain;" />`;

export function generateEmailTemplate(data: EmailTemplateData): string {
  const {
    storeName,
    logoUrl,
    iconUrl,
    headline,
    paragraph,
    subText,
    buttonText,
    buttonLink,
    buttonBackground,
    buttonBorderRadius,
    buttonFontColor,
    buttonFontStyle,
    headlineDesktopFontColor,
    headlineDesktopFontFamily,
    headlineDesktopFontSize,
    headlineDesktopFontWeight,
    headlineFontStyle,
    headlineDesktopLineHeight,
    headlineDesktopLetterSpacing,
    paragraphDesktopFontColor,
    paragraphDesktopFontFamily,
    paragraphDesktopFontSize,
    paragraphDesktopFontWeight,
    paragraphFontStyle,
    paragraphDesktopLineHeight,
    paragraphDesktopLetterSpacing,
    subTextDesktopFontColor,
    subTextDesktopFontFamily,
    subTextDesktopFontSize,
    subTextDesktopFontWeight,
    subTextDesktopFontStyle,
    subTextDesktopLineHeight,
    subTextDesktopLetterSpacing,
    headlineMobileFontColor,
    headlineMobileFontFamily,
    headlineMobileFontSize,
    headlineMobileFontWeight,
    paragraphMobileFontColor,
    paragraphMobileFontFamily,
    paragraphMobileFontSize,
    paragraphMobileFontWeight,
    desktopLogoWidth,
    mobileLogoWidth,
    footerContent,
    togglePoweredByOpEnabled,
    selectedTemplateKey,
    customDomain,
    isPreview,
  } = data;

  const template = `
    <div>
      <div style="margin-bottom: 32px;">
        <div style="display: flex; align-items: center; gap: 2px;">
          <span style="display: flex; align-items: center; margin-right: 12px; max-width: ${desktopLogoWidth}px;">
            ${logoUrl ? `<img src="${logoUrl}" alt="${storeName}" style="display: block; width: auto; max-width: 100%; height: auto; object-fit: contain;" class="logo" />` : DEFAULT_LOGO_SVG}
          </span>
          ${
            !logoUrl
              ? `<span style="font-family: 'Tiro Bangla', serif; font-weight: 400; font-size: 21.95px; line-height: 22px; color: #000; text-align: left; display: flex;">
            ${storeName}
          </span>`
              : ''
          }
        </div>
      </div>

      <div style="display: block;">
        ${
          iconUrl && iconUrl !== logoUrl
            ? `
        <div style="display: block; margin-bottom: 16px;">
          ${iconUrl.startsWith('<') ? iconUrl : `<img src="${iconUrl}" alt="Icon" style="width: 56px; height: 57px; max-width: 100%; max-height: 100%; object-fit: contain" />`}
        </div>
      `
            : ''
        }
        <h1 style="
          color: ${headlineDesktopFontColor};
          font-family: ${headlineDesktopFontFamily};
          font-size: ${headlineDesktopFontSize}px;
          font-weight: ${headlineDesktopFontWeight};
          text-transform: ${headlineFontStyle};
          line-height: ${headlineDesktopLineHeight};
          letter-spacing: ${headlineDesktopLetterSpacing}px;
          text-align: left;
          margin: 0 0 16px 0;
        ">${headline}</h1>

        <p style="
          color: ${paragraphDesktopFontColor};
          font-family: ${paragraphDesktopFontFamily};
          font-size: ${paragraphDesktopFontSize}px;
          font-weight: ${paragraphDesktopFontWeight};
          text-transform: ${paragraphFontStyle};
          line-height: ${paragraphDesktopLineHeight};
          letter-spacing: ${paragraphDesktopLetterSpacing}px;
          text-align: left;
          margin: 0 0 16px 0;
        ">${paragraph}</p>

        ${
          subText
            ? `<div style="
              color: ${subTextDesktopFontColor};
              font-family: ${subTextDesktopFontFamily};
              font-size: ${subTextDesktopFontSize}px;
              font-weight: ${subTextDesktopFontWeight};
              text-transform: ${subTextDesktopFontStyle};
              line-height: ${subTextDesktopLineHeight};
              letter-spacing: ${subTextDesktopLetterSpacing}px;
              margin: 0;
            ">${subText}</div>`
            : ''
        }
        </div>
        ${
          selectedTemplateKey === 'storeCredit'
            ? `
  <div style="background:rgba(248, 245, 255, 0.93); border-radius: 12px; padding: 16px 5em; margin: 32px auto 0 auto; text-align: center;">
    <div style="font-size: 16px; color: #1D2433; margin-bottom: 8px;">Total Value</div>
    <div style="font-size: 40px; font-weight: bold; color: #000; margin-bottom: 16px;">[[totalValue]]</div>
    <div style="background: #fff; border-radius: 8px; padding: 16px; font-size: 20px; font-weight: 600; letter-spacing: 2px; margin-bottom: 12px; border: 1px solid #E1E6EF;">
      <div style="font-size: 14px; color: #A3A9B7; margin-bottom: 8px; font-weight: 400; font-family: ${paragraphDesktopFontFamily};">Your Code</div>
      [[code]]
    </div>
    <div style="font-size: 13px; color: #1D2433; margin-bottom: 16px;">
      Use this to redeem your store credit.<br>
      <span style="color: #5B1FD3; text-decoration: underline; cursor: pointer;">View breakdown of your reimbursement</span>
    </div>
        <a href="${buttonLink}" style="
          display: block;
          background-color: ${buttonBackground};
          border-radius: ${buttonBorderRadius}px;
          color: ${buttonFontColor};
          font-family: ${paragraphDesktopFontFamily};
          font-size: 16px;
          text-transform: ${buttonFontStyle};
          font-weight: ${paragraphDesktopFontWeight};
          text-decoration: none;
          text-align: center;
          min-height: 58px;
          gap: 10px;
          margin: 48px auto 16px 0;
          align-content: center;
        ">${buttonText}
        </a>
  </div>
`
            : ''
        }
        ${
          selectedTemplateKey !== 'storeCredit'
            ? `
        <div style="text-align: left; margin-bottom: 32px;" width="100%">
          <a href="${buttonLink}" style="
            display: block;
            background-color: ${buttonBackground};
            border-radius: ${buttonBorderRadius}px;
            color: ${buttonFontColor};
            font-family: ${paragraphDesktopFontFamily};
            font-size: 16px;
            text-transform: ${buttonFontStyle};
            font-weight: ${paragraphDesktopFontWeight};
            text-decoration: none;
            text-align: center;
            width: 100%;
            height: 58px;
            line-height: 58px;
            margin: 48px auto;
          ">${buttonText}
          </a>
        </div>
        `
            : ''
        }
        <div style="text-align: left; margin-top: 32px;">
          <div style="font-size: 12px; color: #1D2433; margin-bottom: 8px;">
            ${new Date().getFullYear()}, ${footerContent}
            ${
              togglePoweredByOpEnabled
                ? `
          </div>
          <div style="margin-bottom: 8px;">
            <a href="#" style="color: #1D2433; text-decoration: underline; margin-right: 9.6px; font-size: 12px;">Terms</a>
            <a href="#" style="color: #1D2433; text-decoration: underline; font-size: 12px;">File a Claim</a>
          </div>
          <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="margin-top: 16px;">
            <tr>
              <td style="color: #A3A9B7; font-size: 12px; vertical-align: middle; padding-right: 8px; background-color: #FFFFFF !important; padding-left: 0 !important;">
                Powered by
              </td>
              <td style="vertical-align: middle; background-color: #FFFFFF !important;">
                <img
                  src="https://op-media-asset.s3.us-west-1.amazonaws.com/assets/emails/logo-light.png"
                  alt="Order Protection Logo"
                  style="width: 150px; height: 32px; max-width: 100%; max-height: 100%; object-fit: contain; vertical-align: middle;"
                />
              </td>
            </tr>
          </table>
          `
                : ''
            }
        </div>
      </div>
    `;

  // --- UNREAD MESSAGE BLOCK HTML ---
  const unreadMessageBlock = `
    <div style="margin-bottom: 32px; width: 100%; border-radius: 24px; border-bottom-right-radius: 0; background-color: #f1f3f9; padding: 32px; box-sizing: border-box;">
      <p style="margin: 0; font-size: 16px; line-height: 24px; color: #1d2433;">
        Hello [[customerName]],<br /><br />
        [[message]]
      </p>
    </div>
  `;

  const unreadPostButtonText = `
    <p style="margin: 0 0 24px; font-size: 16px; line-height: 24px; color: rgba(29, 36, 51, 0.8);">
      If you haven't received a response within 24 hours, please don't hesitate to reply to this email.
    </p>
  `;

  // --- WARRANTY PRODUCTS BLOCK HTML ---
  const warrantyProductsBlock = `
    <div style="margin: 32px 0 0 0; text-align: left;">
      <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="border-collapse: collapse;">
        <tr style="border-bottom: 1px solid #E1E6EF;">
          <td style="padding: 16px 0; width: 64px; vertical-align: top;">
            <div style="width: 56px; height: 56px; background: #F1F3F9; border-radius: 8px;"></div>
          </td>
          <td style="padding: 16px 0 16px 12px; vertical-align: top;">
            <div style="font-weight: 600; font-size: 14px; color: #1D2433; margin-bottom: 4px;">Sample Product Name</div>
            <div style="font-size: 12px; color: #A3A9B7; margin-bottom: 2px;">SKU: SAMPLE-001</div>
            <div style="font-size: 12px; color: #A3A9B7; margin-bottom: 2px;">Color: Black &middot; Size: M</div>
            <div style="font-size: 12px; color: #A3A9B7;">Qty: 1</div>
          </td>
        </tr>
        <tr>
          <td style="padding: 16px 0; width: 64px; vertical-align: top;">
            <div style="width: 56px; height: 56px; background: #F1F3F9; border-radius: 8px;"></div>
          </td>
          <td style="padding: 16px 0 16px 12px; vertical-align: top;">
            <div style="font-weight: 600; font-size: 14px; color: #1D2433; margin-bottom: 4px;">Another Product Name</div>
            <div style="font-size: 12px; color: #A3A9B7; margin-bottom: 2px;">SKU: SAMPLE-002</div>
            <div style="font-size: 12px; color: #A3A9B7; margin-bottom: 2px;">Color: White &middot; Size: L</div>
            <div style="font-size: 12px; color: #A3A9B7;">Qty: 2</div>
          </td>
        </tr>
      </table>
    </div>
  `;

  // --- REVIEWS BLOCK HTML ---
  const reviewsBlock = `
    <div style="margin: 32px 0 0 0; text-align: left;">
      <p style="margin: 0 0 24px; line-height: 24px; color: rgba(29, 36, 51, 0.8); font-weight: bold;">
        How would you rate your experience with us?
      </p>
      <table align="center" style="margin-left: auto; margin-right: auto; width: 100%; justify-content: flex-start; background-color: #FFFFFF !important; background: #FFFFFF !important;" cellpadding="0" cellspacing="0" role="presentation">
        <tr><td style="padding-bottom: 8px; background-color: #FFFFFF !important; background: #FFFFFF !important;">
          ${!isPreview ? `<a href="[[reviewUrl]]?rating=5">` : ''}
          <img src="https://storage.googleapis.com/cdn.orderprotection.com/branding/email/5-5.png?v=1" style="border: 0; max-width: 100%; vertical-align: middle; line-height: 100%; width: 282px;" alt="5 stars">
          ${!isPreview ? '</a>' : ''}
        </td></tr>
        <tr><td style="padding-bottom: 8px; background-color: #FFFFFF !important; background: #FFFFFF !important;">
          ${!isPreview ? `<a href="[[reviewUrl]]?rating=4">` : ''}
          <img src="https://storage.googleapis.com/cdn.orderprotection.com/branding/email/4-5.png?v=1" style="border: 0; max-width: 100%; vertical-align: middle; line-height: 100%; width: 282px;" alt="4 stars">
          ${!isPreview ? '</a>' : ''}
        </td></tr>
        <tr><td style="padding-bottom: 8px; background-color: #FFFFFF !important; background: #FFFFFF !important;">
          ${!isPreview ? `<a href="[[reviewUrl]]?rating=3">` : ''}
          <img src="https://storage.googleapis.com/cdn.orderprotection.com/branding/email/3-5.png?v=1" style="border: 0; max-width: 100%; vertical-align: middle; line-height: 100%; width: 282px;" alt="3 stars">
          ${!isPreview ? '</a>' : ''}
        </td></tr>
        <tr><td style="padding-bottom: 8px; background-color: #FFFFFF !important; background: #FFFFFF !important;">
          ${!isPreview ? `<a href="[[reviewUrl]]?rating=2">` : ''}
          <img src="https://storage.googleapis.com/cdn.orderprotection.com/branding/email/2-5.png?v=1" style="border: 0; max-width: 100%; vertical-align: middle; line-height: 100%; width: 282px;" alt="2 stars">
          ${!isPreview ? '</a>' : ''}
        </td></tr>
        <tr><td style="padding-bottom: 8px; background-color: #FFFFFF !important; background: #FFFFFF !important;">
          ${!isPreview ? `<a href="[[reviewUrl]]?rating=1">` : ''}
          <img src="https://storage.googleapis.com/cdn.orderprotection.com/branding/email/1-5.png?v=1" style="border: 0; max-width: 100%; vertical-align: middle; line-height: 100%; width: 282px;" alt="1 star">
          ${!isPreview ? '</a>' : ''}
        </td></tr>
      </table>
    </div>
  `;

  // Insert warranty products block before the CTA button for 'extendedWarranty' template
  let templateWithExtras = template;
  if (selectedTemplateKey === 'extendedWarranty') {
    const footerStartIndex = template.indexOf('<div style="text-align: left; margin-top: 32px;">');
    if (footerStartIndex !== -1) {
      templateWithExtras =
        template.slice(0, footerStartIndex) + warrantyProductsBlock + template.slice(footerStartIndex);
    }
  }

  // Insert unread message block after the headline, and post-button text before footer
  if (selectedTemplateKey === 'unread') {
    const headlineEndIndex = templateWithExtras.indexOf('</h1>');
    if (headlineEndIndex !== -1) {
      const insertPos = headlineEndIndex + '</h1>'.length;
      templateWithExtras =
        templateWithExtras.slice(0, insertPos) + unreadMessageBlock + templateWithExtras.slice(insertPos);
    }
    const footerStartIndex = templateWithExtras.indexOf('<div style="text-align: left; margin-top: 32px;">');
    if (footerStartIndex !== -1) {
      templateWithExtras =
        templateWithExtras.slice(0, footerStartIndex) +
        unreadPostButtonText +
        templateWithExtras.slice(footerStartIndex);
    }
  }

  // Insert reviews block before the footer for 'approved' template
  let templateWithReviews = templateWithExtras;
  if (selectedTemplateKey === 'approved') {
    // Insert reviews block before the footer div (before "text-align: left; margin-top: 32px;")
    const footerStartIndex = template.indexOf('<div style="text-align: left; margin-top: 32px;">');
    if (footerStartIndex !== -1) {
      templateWithReviews = template.slice(0, footerStartIndex) + reviewsBlock + template.slice(footerStartIndex);
    } else {
      // Fallback: insert before the last closing </div>
      const lastDivIndex = template.lastIndexOf('</div>');
      if (lastDivIndex !== -1) {
        templateWithReviews = template.slice(0, lastDivIndex) + reviewsBlock + template.slice(lastDivIndex);
      } else {
        templateWithReviews += reviewsBlock;
      }
    }
  }

  return wrapEmailDocument(templateWithReviews, data);
}

/** Document-level styling values the email `<head>` needs (`:root` CSS vars + mobile logo width). */
export interface EmailDocumentVars {
  headlineDesktopFontColor: string;
  headlineDesktopFontFamily: string;
  headlineDesktopFontSize: number;
  headlineDesktopFontWeight: number | string;
  headlineMobileFontColor: string;
  headlineMobileFontFamily: string;
  headlineMobileFontSize: number;
  headlineMobileFontWeight: number | string;
  paragraphDesktopFontColor: string;
  paragraphDesktopFontFamily: string;
  paragraphDesktopFontSize: number;
  paragraphDesktopFontWeight: number | string;
  paragraphMobileFontColor: string;
  paragraphMobileFontFamily: string;
  paragraphMobileFontSize: number;
  paragraphMobileFontWeight: number | string;
  mobileLogoWidth: number;
}

/**
 * Wraps an email body fragment in the full standalone HTML document (head styles, :root vars,
 * mobile media queries, container). Shared by the legacy flat-form compiler (generateEmailTemplate)
 * and the block compiler (renderEmailFromBlocks) so the saved/sent document chrome is identical
 * regardless of which path produced the body.
 */
export function wrapEmailDocument(bodyHtml: string, vars: EmailDocumentVars): string {
  const {
    headlineDesktopFontColor,
    headlineDesktopFontFamily,
    headlineDesktopFontSize,
    headlineDesktopFontWeight,
    headlineMobileFontColor,
    headlineMobileFontFamily,
    headlineMobileFontSize,
    headlineMobileFontWeight,
    paragraphDesktopFontColor,
    paragraphDesktopFontFamily,
    paragraphDesktopFontSize,
    paragraphDesktopFontWeight,
    paragraphMobileFontColor,
    paragraphMobileFontFamily,
    paragraphMobileFontSize,
    paragraphMobileFontWeight,
    mobileLogoWidth,
  } = vars;

  return `
<!DOCTYPE html>
<html lang="en">
<head>
<style>
  :root {
    --headline-desktop-color: ${headlineDesktopFontColor};
    --headline-desktop-family: ${headlineDesktopFontFamily};
    --headline-desktop-size: ${headlineDesktopFontSize}px;
    --headline-desktop-weight: ${headlineDesktopFontWeight};
    --headline-mobile-color: ${headlineMobileFontColor};
    --headline-mobile-family: ${headlineMobileFontFamily};
    --headline-mobile-size: ${headlineMobileFontSize}px;
    --headline-mobile-weight: ${headlineMobileFontWeight};
    --paragraph-desktop-color: ${paragraphDesktopFontColor};
    --paragraph-desktop-family: ${paragraphDesktopFontFamily};
    --paragraph-desktop-size: ${paragraphDesktopFontSize}px;
    --paragraph-desktop-weight: ${paragraphDesktopFontWeight};
    --paragraph-mobile-color: ${paragraphMobileFontColor};
    --paragraph-mobile-family: ${paragraphMobileFontFamily};
    --paragraph-mobile-size: ${paragraphMobileFontSize}px;
    --paragraph-mobile-weight: ${paragraphMobileFontWeight};
  }
</style>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>Order Protection Email</title>
  <!--[if mso]>
  <style type="text/css">
    body, table, td {font-family: Arial, Helvetica, sans-serif !important;}
  </style>
  <![endif]-->
  <style>
    body {
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
      word-wrap: break-word;
      word-break: break-word;
    }
    .container {
      width: 100%;
      margin: 0 auto;
      padding: 40px;
      box-sizing: border-box;
    }
    .content-wrapper {
      width: 100%;
      max-width: 100%;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
    }
    h1 {
      padding: 0;
      width: 100%;
      max-width: 100%;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
    }
    p {
      padding: 0;
      width: 100%;
      max-width: 100%;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
    }
    .button-wrapper {
      width: 100%;
      max-width: 100%;
      margin: 0 0 32px;
      text-align: center;
    }
    .button {
      display: inline-block;
      padding: 12px 24px;
      text-decoration: none;
      text-align: center;
      max-width: 100%;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
    }
    /* Remove borders between table rows */

    table tr:not(:last-child) {
      border-color: #F1F3F9 !important;
    }
    @media only screen and (max-width: 600px) {
      .container {
        width: 100% !important;
        padding: 20px !important;
      }
      .logo {
        width: auto !important;
        max-width: ${mobileLogoWidth}px !important;
        height: auto !important;
      }
      .button {
        width: 100% !important;
        text-align: center !important;
      }
      h1 {
        color: var(--headline-mobile-color) !important;
        font-family: var(--headline-mobile-family) !important;
        font-size: var(--headline-mobile-size) !important;
        font-weight: var(--headline-mobile-weight) !important;
      }
      p {
        color: var(--paragraph-mobile-color) !important;
        font-family: var(--paragraph-mobile-family) !important;
        font-size: var(--paragraph-mobile-size) !important;
        font-weight: var(--paragraph-mobile-weight) !important;
      }
    }
  </style>
</head>
<body style="margin: 0; padding: 0; font-family: Geist, Arial, sans-serif;">
  <div class="container">
    <div class="content-wrapper">
      ${bodyHtml}
    </div>
  </div>
</body>
</html>`;
}

/**
 * Compiles a flat list of email blocks into the full standalone HTML document using each block's
 * catalog render() function. This is the block-builder equivalent of generateEmailTemplate, and the
 * single source of truth shared with the WYSIWYG preview (which renders the same per-block fragments).
 */
export function renderEmailFromBlocks(
  blocks: PersistedEmailBlock[],
  ctx: EmailRenderContext,
  vars: EmailDocumentVars,
): string {
  const catalog = getEmailCatalog();

  const renderBlock = (block: PersistedEmailBlock): string => {
    const schema = catalog[block.type];
    if (!schema) return '';
    const childrenHtml = block.children?.length ? block.children.map(renderBlock).join('\n') : '';

    return schema.render(block.attributes, ctx, childrenHtml);
  };

  const body = blocks.map(renderBlock).join('\n');

  return wrapEmailDocument(body, vars);
}
